import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import payrollnootLogoLg from "assets/images/payrollnoot-logo-lg.png";
import routes from "constants/routes";

const tenants = [
    {
        key: "company",
        label: "Company",
        description: "Run payroll, manage employees, and company settings.",
        badge: "Admin Access",
        icon: "🏢",
        gradient: "linear-gradient(135deg, #f39b12, #dd6f00)",
        route: routes.LOGIN.COMPANY,
    },
    {
        key: "accountant",
        label: "Accountant",
        description: "Run payroll for multiple client companies from one login.",
        badge: "Multi-Company",
        icon: "💼",
        gradient: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
        route: `${routes.LOGIN.HOME}/accountant`,
    },
    {
        key: "reseller",
        label: "Partner / Reseller",
        description: "Manage client companies, billing, and white-label branding.",
        badge: "Firm Portal",
        icon: "🤝",
        gradient: "linear-gradient(135deg, #0f766e, #134e4a)",
        route: `${routes.LOGIN.HOME}/reseller`,
    },
    {
        key: "employee",
        label: "Employee",
        description: "View pay stubs, tax forms, and account details.",
        badge: "Self Service",
        icon: "👤",
        gradient: "linear-gradient(135deg, #35c3ff, #2d7ef7)",
        route: routes.LOGIN.EMPLOYEE,
    },
    {
        key: "contractor",
        label: "Contractor",
        description: "View payments, documents, and profile details.",
        badge: "Contractor",
        icon: "📋",
        gradient: "linear-gradient(135deg, #34d399, #22c55e)",
        route: `${routes.LOGIN.HOME}/contractor`,
    },
];

const featureList = [
    "Free Direct Deposit",
    "Free Electronic W2s & 1099s*",
    "Automatic Tax Filings",
    "Unlimited Payroll Runs",
    "AccountingNoot Integration (Coming Soon)",
    "Benefits & Deductions Management",
    "Automatic Child Support Remittance",
    "Hybrid Work-from-home Payrolls",
    "Multi-payrate Payrolls",
    "Workers Comp*",
];

const DashboardMockup = () => (
    <div style={{
        width: 580, height: 300,
        background: '#fff', border: '1px solid #ddd', borderRadius: 6,
        overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.10)', flexShrink: 0,
    }}>
        {/* Top bar */}
        <div style={{ height: 22, borderBottom: '1px solid #ececec', background: '#fafafa' }} />
        <div style={{ display: 'flex', height: 'calc(100% - 22px)' }}>
            {/* Sidebar */}
            <div style={{ width: 68, borderRight: '1px solid #efefef', background: '#fcfcfc', padding: '10px 8px' }}>
                {[...Array(6)].map((_, i) => (
                    <div key={i} style={{ height: 7, background: '#e7e7e7', borderRadius: 6, marginBottom: 10 }} />
                ))}
            </div>
            {/* Main area */}
            <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Cards row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                    {[...Array(8)].map((_, i) => (
                        <div key={i} style={{ height: 46, background: '#faf4ef', border: '1px solid #f1e7df', borderRadius: 5 }} />
                    ))}
                </div>
                {/* Lower row */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10, flex: 1 }}>
                    {/* Bar chart */}
                    <div style={{ border: '1px solid #efefef', borderRadius: 5, overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height: '100%', padding: '16px 18px' }}>
                            <div style={{ width: 38, height: 36, background: 'linear-gradient(to top, #f4b37a, #ef7f0f)', borderRadius: '3px 3px 0 0' }} />
                            <div style={{ width: 38, height: 90, background: 'linear-gradient(to top, #f4b37a, #ef7f0f)', borderRadius: '3px 3px 0 0' }} />
                            <div style={{ width: 38, height: 36, background: 'linear-gradient(to top, #f4b37a, #ef7f0f)', borderRadius: '3px 3px 0 0' }} />
                        </div>
                    </div>
                    {/* Donut */}
                    <div style={{ border: '1px solid #efefef', borderRadius: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 12, gap: 8 }}>
                        <div style={{
                            width: 80, height: 80, borderRadius: '50%',
                            background: 'conic-gradient(#0d66a8 0 72%, #c96d19 72% 100%)',
                            position: 'relative',
                        }}>
                            <div style={{ position: 'absolute', inset: 16, background: '#fff', borderRadius: '50%' }} />
                        </div>
                        {[...Array(3)].map((_, i) => (
                            <div key={i} style={{ height: 8, width: '80%', background: '#ececec', borderRadius: 8 }} />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    </div>
);

const MultiTenantLogin = () => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);

    useEffect(() => {
        const host = window.location.hostname.toLowerCase();
        const isTenant =
            host.endsWith('.payrollnoot.com')
            && host !== 'www.payrollnoot.com'
            && !host.startsWith('api.');
        if (isTenant) {
            navigate('/login/company', { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    return (
        <div style={{ display: 'flex', minHeight: '100vh', width: '100%', background: '#f3f3f3', fontFamily: 'Arial, Helvetica, sans-serif' }}>

            {/* ── LEFT PANEL — hidden on mobile ── */}
            <div style={{ flex: 1, padding: '28px 40px 40px', display: isMobile ? 'none' : 'flex', flexDirection: 'column', background: '#f3f3f3', minWidth: 0 }}>
                <img src={payrollnootLogoLg} alt="PayrollNoot" style={{ height: 60, objectFit: 'contain', objectPosition: 'left', marginBottom: 28 }} />

                <div style={{ maxWidth: 1000, margin: '0 auto', width: '100%' }}>
                    <h1 style={{ fontSize: 30, fontWeight: 800, marginBottom: 10, color: '#111', textAlign: 'center' }}>
                        All Inclusive Payroll Pricing
                    </h1>
                    <p style={{ fontSize: 17, lineHeight: 1.5, marginBottom: 28, color: '#333', textAlign: 'center', maxWidth: 800, margin: '0 auto 28px' }}>
                        We offer transparent pricing with no extra fees so you can be confident that you're getting the best value for your business.
                    </p>

                    {/* Features + Mockup */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: 40, flexWrap: 'wrap' }}>
                        {/* Feature list */}
                        <div style={{ fontSize: 17, lineHeight: 1.5, color: '#111', minWidth: 220 }}>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {featureList.map(item => {
                                    const isComingSoon = item.includes('Coming Soon');
                                    return (
                                        <li key={item} style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <span style={{ color: isComingSoon ? '#9ca3af' : '#E7730A', fontWeight: 700 }}>
                                                {isComingSoon ? '◷' : '✓'}
                                            </span>
                                            <span style={{ color: isComingSoon ? '#9ca3af' : 'inherit' }}>{item}</span>
                                        </li>
                                    );
                                })}
                            </ul>
                            <p style={{ fontSize: 12, marginTop: 6, color: '#888' }}>*additional cost</p>
                        </div>

                        {/* Dashboard mockup */}
                        <DashboardMockup />
                    </div>
                </div>
            </div>

            {/* ── RIGHT PANEL ── */}
            <div style={{
                width: isMobile ? '100%' : 400,
                background: '#f3f3f3',
                borderLeft: isMobile ? 'none' : '5px solid #17203a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: isMobile ? '40px 24px' : '30px 28px',
                flexShrink: 0,
            }}>
                <div style={{ width: '100%', maxWidth: 330, textAlign: 'center' }}>
                    <img src={payrollnootLogoLg} alt="PayrollNoot" style={{ height: 60, objectFit: 'contain', marginBottom: 24 }} />

                    <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 6, color: '#1a2442' }}>Welcome back</div>
                    <div style={{ color: '#7b8496', fontSize: 14, marginBottom: 24 }}>Choose your portal and jump right in.</div>

                    {/* Portal options */}
                    {tenants.map(tenant => (
                        <button
                            key={tenant.key}
                            onClick={() => navigate(tenant.route)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 12,
                                width: '100%', background: '#f8f8f8',
                                border: '2px solid #e7e7e7', borderRadius: 16,
                                padding: '14px 12px', marginBottom: 12,
                                cursor: 'pointer', textAlign: 'left',
                                transition: 'all 0.2s',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#dcdcdc'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; e.currentTarget.style.borderColor = '#e7e7e7'; }}
                        >
                            <div style={{
                                width: 40, height: 40, borderRadius: 10,
                                background: tenant.gradient,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 18, flexShrink: 0,
                            }}>
                                {tenant.icon}
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                    <span style={{ fontSize: 13, fontWeight: 800, color: '#27314c' }}>{tenant.label}</span>
                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#7b8496', textTransform: 'uppercase' }}>{tenant.badge}</span>
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.35 }}>{tenant.description}</div>
                            </div>
                            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" style={{ color: '#b8bcc6', flexShrink: 0 }}>
                                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    ))}

                    <div style={{ marginTop: 16, fontSize: 13, color: '#9aa2b1' }}>
                        Need help signing in?{' '}
                        <span style={{ color: '#f08a16', fontWeight: 700, cursor: 'pointer' }}>Contact support</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiTenantLogin;
